import './TagWorkstreamModal.scss';

import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import AvatarList from '@/src/components/common/Avatar/List/List';
import Button from '@/src/components/common/Elements/Button/Button';
import { MODAL_TYPE } from '@/src/components/common/Elements/Modal/Config';
import ModalPopup from '@/src/components/common/Elements/Modal/ModalPopup/ModalPopup';
import SearchWorkstreamDropdown from '@/src/components/project/Common/Fields/SearchWorkstreamDropdown/SearchWorkstreamDropdown';
import {
  addWorkstream,
  removeWorkstream,
  toggleNoticeModal,
} from '@/src/reducers/project/tagFileWorkstream/tagFileWorkstream.actions';
import { RootState } from '@/src/reducers/types';
import { searchWorkstream } from '@/src/reducers/workstream/workstream.service';

import NoticeModal from './NoticeModal/NoticeModal';

type TagWorkstreamModalProps = {
  title: string;
  onClose: () => void;
  onSave: () => void;
};

const TagWorkstreamModal = ({ title, onClose, onSave }: TagWorkstreamModalProps) => {
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const loggedUser = useSelector((store: RootState) => store.loggedUser);
  const tagFileWorkstream = useSelector((store: RootState) => store.tagFileWorkstream);
  const { selectedWorkstreams, showNoticeModal } = tagFileWorkstream;

  const handleSearchWorkstream = async (workstreamName) => {
    const { onSearchWorkstream } = tagFileWorkstream;

    if (onSearchWorkstream) {
      // TODO: ask BE to stabdardize return format
      // workstream_members -> workstreamMembers and response.data.result format
      return onSearchWorkstream(workstreamName);
      // const response = await onSearchWorkstream(workstreamName)

      // return {
      //     data: {
      //         result: response.results
      //     }
      // }
    } else {
      const params = {
        page: 1,
        projectId: projectId,
        userId: loggedUser.data.id,
        workstream: workstreamName,
        userRole: loggedUser.data.userRole,
      };

      return searchWorkstream(params);
    }
  };

  const handleSelectWorkstream = (workstream) => {
    dispatch(addWorkstream(workstream));
  };

  const handleRemove = (workstream) => {
    if (!workstream.required) {
      dispatch(removeWorkstream(workstream));
    } else {
      dispatch(toggleNoticeModal(true));
    }
  };

  const checkChanges = () => {
    const { selectedDocuments, selectedWorkstreams } = tagFileWorkstream;
    const workstreamsinDocuments = {};
    const currentWorkstreams = {};

    for (const document of selectedDocuments) {
      if (!document.tagWorkstream) {
        continue;
      }

      for (const workstream of document.tagWorkstream) {
        workstreamsinDocuments[workstream.value] = 1;
      }
    }

    for (const workstream of selectedWorkstreams) {
      currentWorkstreams[workstream.value] = !workstream.isRemoved && 1;
    }

    return !_.isEqual(workstreamsinDocuments, currentWorkstreams);
  };

  const saveEnabled = checkChanges();

  return (
    <React.Fragment>
      <ModalPopup
        title={title}
        modalType={MODAL_TYPE.inverse}
        onClose={onClose}
        onConfirm={onSave}
        confirmBtn={{ label: 'Save', disabled: selectedWorkstreams.length === 0 && !saveEnabled }}
        id={'tagFileWorkstream'}
        className={'tag-file-modal'}
      >
        <SearchWorkstreamDropdown
          onSearchWorkstream={handleSearchWorkstream}
          onSelect={handleSelectWorkstream}
          exclude={selectedWorkstreams.map((w) => w.value)}
          disableButton={true}
          className="tagWorkstreamModal"
          projectId={projectId}
        />

        <div
          className={clsx(
            'container-taggedWorkstreamList',
            'tw-mt-[15px]',
            'tw-h-[250px] tw-overflow-y-auto tw-scroll-styles'
          )}
        >
          {selectedWorkstreams.map((w) => (
            <div
              key={`tagWorkstreamListItem_${w.value}`}
              className={clsx(
                'container--taggedItem',
                'tw-flex tw-items-center tw-justify-between',
                'tw-px-[25px] tw-py-[10px]',
                'tw-border-solid tw-border-b-[1px] tw-border-b-cfo-grey-500',
                w.isRemoved &&
                  'removed tw-border-[1px] tw-border-cfo-grey-500 tw-bg-cfo-grey-100 tw-rounded-[10px]'
              )}
            >
              <div
                className={clsx(
                  'container--taggedFileDetails',
                  'tw-flex tw-items-center',
                  'tw-max-w-[300px] tw-w-full'
                )}
              >
                <div
                  className={clsx(
                    'container--workstreamIndicator',
                    'tw-w-[10px] tw-h-[10px]',
                    'tw-rounded-[2px]',
                    'tw-mr-[10px]'
                  )}
                  style={{ background: `${w.color}` }}
                ></div>
                <p className={clsx('tw-text-cfo-black')}>{w.label}</p>
              </div>

              <div className={clsx('container--actions', 'tw-flex tw-items-center')}>
                <AvatarList
                  idKey="user.id"
                  id={w.id}
                  avatars={w.member}
                  renderer={(m) =>
                    `${(m.user || {}).user.firstName} ${(m.user || {}).user.lastName}`
                  }
                />

                <Button
                  className={clsx(
                    'tw-py-[6px] tw-px-[12px] tw-ml-[25px]',
                    'tw-rounded-[5px]',
                    'tw-font-brandonTextMedium',
                    w.isRemoved
                      ? 'tw-bg-cfo-grey-500 tw-text-cfo-grey-600 tw-pointer-events-none tw-cursor-not-allowed'
                      : w.isPermanent
                      ? 'tw-bg-cfo-grey-500 tw-text-cfo-grey-600 hover:tw-text-cfo-grey-600 hover:tw-bg-cfo-grey-500'
                      : 'tw-bg-cfo-red-100 tw-text-cfo-red-400 hover:tw-text-white hover:tw-bg-cfo-red-400'
                  )}
                  onClick={() => handleRemove(w)}
                  disabled={w.isPermanent}
                  tooltip={w.isPermanent ? { content: 'Unable to remove tag' } : null}
                >
                  {w.isRemoved ? 'Removed' : 'Remove'}
                </Button>
              </div>
            </div>
          ))}
          {selectedWorkstreams.length === 0 && (
            <span className={clsx('tw-text-cfo-black')}>No tagged workstreams</span>
          )}
        </div>
      </ModalPopup>
      {showNoticeModal && <NoticeModal />}
    </React.Fragment>
  );
};

export default TagWorkstreamModal;
